import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  Edit,
  Save,
  X,
  Upload,
  Trash2,
  User,
  BookOpen,
  Heart,
} from "lucide-react";
import api from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import homePageImage from "../assets/home-page-image.png";
import desertMonasticSayings from "../data/desertMonasticSayings.json";
import HomePageAdCarousel from "../components/HomePageAdCarousel";

const Home = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [homePageAds, setHomePageAds] = useState([]);
  const [showAdPopup, setShowAdPopup] = useState(false);
  const [vicarData, setVicarData] = useState({
    name: "",
    image: "",
    message1: "",
    title: "",
  });
  const [isEditingVicar, setIsEditingVicar] = useState(false);
  const [editVicarForm, setEditVicarForm] = useState({ ...vicarData });
  const [imageFile, setImageFile] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogType, setDialogType] = useState("success");
  const [dailyVerse, setDailyVerse] = useState(null);
  const [verseLoading, setVerseLoading] = useState(true);
  const [dailyQuote, setDailyQuote] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showVignette, setShowVignette] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [showSubtitle, setShowSubtitle] = useState(false);
  const [showButtons, setShowButtons] = useState(false);

  useEffect(() => {
    fetchData();
    fetchDailyVerse();
    setDailyDesertFatherQuote();
    fetchHomePageAds();

    // Preload the home page image
    const img = new Image();
    img.onload = () => {
      setImageLoaded(true);
      // Start the animation sequence once image is loaded
      setTimeout(() => setShowVignette(true), 800);
      setTimeout(() => setShowTitle(true), 800);
      setTimeout(() => setShowSubtitle(true), 1400);
      setTimeout(() => setShowButtons(true), 2200);
    };
    img.src = homePageImage;
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, newsRes, vicarRes, servicesRes] = await Promise.all([
        api.get("/api/events?limit=3"),
        api.get("/api/news?limit=3"),
        api.get("/api/vicar").catch(() => ({ data: null })),
        api.get("/api/services").catch(() => ({ data: [] })),
      ]);
      setEvents(Array.isArray(eventsRes.data) ? eventsRes.data : []);
      setNews(Array.isArray(newsRes.data) ? newsRes.data : []);
      if (vicarRes.data) {
        setVicarData(vicarRes.data);
        setEditVicarForm(vicarRes.data);
      }
      if (servicesRes.data && Array.isArray(servicesRes.data)) {
        // Filter regular services and limit to first 3
        const regularServices = servicesRes.data
          .filter((service) => service.category === "regular")
          .slice(0, 3);
        setServices(regularServices);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHomePageAds = async () => {
    try {
      // Check if ads have been shown in this session
      const adsShownInSession = sessionStorage.getItem("homePageAdsShown");

      if (adsShownInSession === "true") {
        return; // Don't show ads again in this session
      }

      const response = await api.get("/api/home-page-ads/active");
      const activeAds = Array.isArray(response.data) ? response.data : [];

      if (activeAds.length > 0) {
        setHomePageAds(activeAds);
        // Show popup after a short delay (3 seconds) to let the page load
        setTimeout(() => {
          setShowAdPopup(true);
        }, 2000);
      }
    } catch (error) {
      console.error("Error fetching home page ads:", error);
    }
  };

  const handleCloseAdPopup = () => {
    setShowAdPopup(false);
    // Mark that ads have been shown in this session
    sessionStorage.setItem("homePageAdsShown", "true");
  };

  const fetchDailyVerse = async () => {
    try {
      setVerseLoading(true);

      // Using Our Manna API for daily Bible verse
      const response = await fetch(
        "https://beta.ourmanna.com/api/v1/get/?format=json&order=daily"
      );
      const data = await response.json();

      if (data && data.verse) {
        setDailyVerse({
          text: data.verse.details.text,
          reference: data.verse.details.reference,
          version: data.verse.details.version || "WEB",
        });
      }
    } catch (error) {
      console.error("Error fetching daily verse:", error);
      // Fallback verse if API fails
      setDailyVerse({
        text: "For God so loved the world that he gave his one and only Son, that whoever believes in him shall not perish but have eternal life.",
        reference: "John 3:16",
        version: "NIV",
      });
    } finally {
      setVerseLoading(false);
    }
  };

  const setDailyDesertFatherQuote = () => {
    // Get today's date as seed for consistent daily quote
    const today = new Date();
    const dayOfYear = Math.floor(
      (today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24)
    );

    // Use day of year as seed to get consistent daily quote
    const quoteIndex = dayOfYear % desertMonasticSayings.sayings.length;
    setDailyQuote(desertMonasticSayings.sayings[quoteIndex]);
  };

  const handleVicarEdit = () => {
    setIsEditingVicar(true);
  };

  const handleVicarCancel = () => {
    setIsEditingVicar(false);
    setEditVicarForm({ ...vicarData });
    setImageFile(null);
  };

  const handleVicarInputChange = (e) => {
    const { name, value } = e.target;
    setEditVicarForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditVicarForm((prev) => ({
          ...prev,
          image: e.target.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageDelete = () => {
    setEditVicarForm((prev) => ({
      ...prev,
      image: "",
    }));
    setImageFile(null);
  };

  const showCustomDialog = (message, type = "success") => {
    setDialogMessage(message);
    setDialogType(type);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setDialogMessage("");
  };

  const handleVicarSave = async () => {
    try {
      console.log("Starting vicar save process...");
      let updatedData = { ...editVicarForm };

      // Validate required fields
      if (!updatedData.name || !updatedData.title) {
        showCustomDialog(
          "Please fill in all required fields (Name and Title)",
          "error"
        );
        return;
      }

      if (imageFile) {
        console.log("Uploading image...");

        try {
          // Step 1: Get upload signature
          const signatureResponse = await api.post("/api/upload/signature");
          const { signature, timestamp, cloudName, apiKey, uploadParams } =
            signatureResponse.data;

          // Step 2: Upload directly to Cloudinary
          const formData = new FormData();
          formData.append("file", imageFile);
          formData.append("signature", signature);
          formData.append("timestamp", timestamp);
          formData.append("api_key", apiKey);

          // Add upload parameters
          Object.keys(uploadParams).forEach((key) => {
            if (key !== "timestamp") {
              formData.append(key, uploadParams[key]);
            }
          });

          const cloudinaryResponse = await fetch(
            `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
            {
              method: "POST",
              body: formData,
            }
          );

          if (!cloudinaryResponse.ok) {
            throw new Error("Failed to upload image to Cloudinary");
          }

          const cloudinaryResult = await cloudinaryResponse.json();
          console.log("Image upload response:", cloudinaryResult);
          updatedData.image = cloudinaryResult.secure_url;
        } catch (error) {
          console.error("Vicar image upload error:", error);
          throw new Error("Failed to upload vicar image. Please try again.");
        }
      }

      console.log("Sending vicar update request with data:", updatedData);

      const response = await api.put("/api/vicar", updatedData);
      console.log("Vicar update response:", response.data);

      setVicarData(response.data);
      setIsEditingVicar(false);
      setImageFile(null);
      showCustomDialog("Vicar information updated successfully!");
    } catch (error) {
      console.error("Error updating vicar data:", error);
      console.error("Error response:", error.response?.data);

      let errorMessage = "Failed to update vicar information";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showCustomDialog(errorMessage, "error");
    }
  };

  return (
    <div className="min-h-screen">
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Loading State */}
        <div
          className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
            imageLoaded ? "opacity-90" : "opacity-0"
          }`}
          style={{ backgroundImage: `url(${homePageImage})` }}
        ></div>

        {/* Vignette Effect Overlay */}
        <div
          className={`absolute inset-0 transition-opacity duration-1000 ${
            showVignette ? "opacity-100" : "opacity-0"
          }`}
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.6) 100%)",
          }}
        ></div>

        {/* Loading Spinner */}
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
          </div>
        )}

        <div className="relative z-10 text-center text-white mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl h-full flex flex-col justify-center">
          <div
            className="flex flex-col justify-center"
            style={{ flex: "0.5", bottom: "25px", position: "relative" }}
          >
            {/* Animated Title */}
            <h1
              className={`max-w-2xl text-3xl sm:text-4xl md:text-5xl lg:text-[4rem] font-bold mb-3 sm:mb-3 leading-tight drop-shadow-lg transform transition-all duration-1000 ease-in ${
                showTitle
                  ? "translate-x-0 opacity-100"
                  : "-translate-x-full opacity-0"
              }`}
            >
              St. Mary's Indian Orthodox Church
            </h1>

            {/* Animated Subtitle */}
            <p
              className={`text-base sm:text-lg md:text-[1rem] mb-8 sm:mb-10 mx-auto leading-relaxed max-w-2xl drop-shadow-md transform transition-all duration-1000 ease-in ${
                showSubtitle
                  ? "translate-x-0 opacity-100"
                  : "-translate-x-full opacity-0"
              }`}
            >
              Preserving traditions, building community, strengthening faith
            </p>
          </div>

          {/* Animated Buttons */}
          <div className="pb-8 sm:pb-0">
            <div
              className={`flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center max-w-lg mx-auto sm:max-w-none transform transition-all duration-800 ease-out ${
                showButtons
                  ? "translate-y-0 opacity-100 scale-100"
                  : "translate-y-8 opacity-0 scale-95"
              }`}
            >
              <a
                href="https://www.youtube.com/@StMarysIndianOrthodoxChurchSAT"
                target="_blank"
                rel="noopener noreferrer"
                className="w-[80%] sm:w-[190px] bg-white border-2 text-red-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-300 text-base sm:text-lg shadow-lg focus:outline-none md:hover:border-white md:hover:text-white md:hover:bg-red-600 md:hover:scale-105 active:bg-red-600 active:text-white active:scale-95 md:active:bg-white md:active:text-red-600 md:active:scale-100"
              >
                Live Streaming
              </a>
              <Link
                to="/services"
                className="w-[80%] sm:w-[190px] bg-white border-2 text-red-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-300 text-base sm:text-lg shadow-lg focus:outline-none md:hover:border-white md:hover:text-white md:hover:bg-red-600 md:hover:scale-105 active:bg-red-600 active:text-white active:scale-95 md:active:bg-white md:active:text-red-600 md:active:scale-100"
              >
                Service Times
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Vicar's Message Section */}
      <section id="welcome-message" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-4">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Welcome Message
              </h2>
              {user && (
                <button
                  onClick={isEditingVicar ? handleVicarCancel : handleVicarEdit}
                  className="mb-4 p-2 text-gray-600 hover:text-red-600 transition-colors"
                >
                  {isEditingVicar ? (
                    <X className="w-6 h-6" />
                  ) : (
                    <Edit className="w-6 h-6" />
                  )}
                </button>
              )}
            </div>
            <div className="w-24 h-1 bg-red-600 mx-auto"></div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading welcome message...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Image Section */}
              <div className="relative">
                {(isEditingVicar ? editVicarForm.image : vicarData.image) ? (
                  <img
                    src={isEditingVicar ? editVicarForm.image : vicarData.image}
                    alt={isEditingVicar ? editVicarForm.name : vicarData.name}
                    className="w-full max-w-[18rem] ml-auto mr-[3rem] rounded-lg shadow-lg object-cover"
                  />
                ) : (
                  <div className="w-full max-w-md mx-auto h-80 bg-gray-200 rounded-lg shadow-lg flex items-center justify-center">
                    <User className="w-24 h-24 text-gray-400" />
                  </div>
                )}
                {isEditingVicar && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg">
                    <div className="flex space-x-4">
                      <label className="bg-white p-3 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                        <Upload className="w-6 h-6 text-gray-600 mx-auto mb-2" />
                        <span className="text-sm text-gray-600">Upload</span>
                        <span className="text-xs text-gray-400 block mt-1">
                          Max 20MB
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </label>
                      {(editVicarForm.image || vicarData.image) && (
                        <button
                          onClick={handleImageDelete}
                          className="bg-white p-3 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <Trash2 className="w-6 h-6 text-red-600 mx-auto mb-2" />
                          <span className="text-sm text-gray-600">Delete</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Content Section */}
              <div>
                {isEditingVicar ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={editVicarForm.name}
                        onChange={handleVicarInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="Enter name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={editVicarForm.title}
                        onChange={handleVicarInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="Enter title/position"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message (Optional)
                      </label>
                      <textarea
                        name="message1"
                        value={editVicarForm.message1}
                        onChange={handleVicarInputChange}
                        rows="6"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="Enter the message (optional)"
                      />
                    </div>
                    <div className="flex space-x-3 pt-4">
                      <button
                        onClick={handleVicarSave}
                        className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors flex items-center"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </button>
                      <button
                        onClick={handleVicarCancel}
                        className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 transition-colors flex items-center"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {vicarData.name ? (
                      <>
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">
                          {vicarData.name}
                        </h3>
                        <p className="text-lg font-semibold text-red-600 mb-6">
                          {vicarData.title}
                        </p>
                        {vicarData.message1 && (
                          <p className="text-lg text-gray-600 leading-relaxed">
                            {vicarData.message1}
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="text-gray-500 text-center">
                        <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-600 mb-2">
                          No Welcome Message Available
                        </h3>
                        <p className="text-gray-500 mb-6">
                          A welcome message from our clergy will appear here
                          once added.
                        </p>
                        {user && (
                          <p className="text-sm">
                            Click the edit button above to add a welcome
                            message.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Daily Bible Verse Section */}
      <section id="daily-verse" className="py-16 bg-red-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Bible Verse of the Day
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto"></div>
          </div>

          {verseLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading today's verse...</p>
            </div>
          ) : dailyVerse ? (
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <BookOpen className="w-12 h-12 text-red-600 mx-auto mb-6" />
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic leading-relaxed">
                "{dailyVerse.text}"
              </blockquote>
              <cite className="text-red-600 font-semibold text-lg">
                - {dailyVerse.reference} ({dailyVerse.version})
              </cite>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-6" />
              <p className="text-gray-500">
                Unable to load today's verse. Please try again later.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Daily Desert Monastic Wisdom Section */}
      <section id="daily-monastic-wisdom" className="py-16 bg-blue-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Daily Desert Monastic Wisdom
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto"></div>
          </div>

          {dailyQuote ? (
            <div className="bg-white rounded-lg shadow-lg p-8">
              <Heart className="w-12 h-12 text-red-600 mx-auto mb-6" />
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic leading-relaxed text-center">
                "{dailyQuote.text}"
              </blockquote>
              <div className="text-center">
                <cite className="text-red-600 font-semibold text-lg">
                  - {dailyQuote.author}
                </cite>
                <p className="text-gray-500 text-sm mt-2">
                  Theme: {dailyQuote.theme}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <Heart className="w-12 h-12 text-gray-400 mx-auto mb-6" />
              <p className="text-gray-500">Loading today's wisdom...</p>
            </div>
          )}
        </div>
      </section>

      <section id="service-times" className="py-20 bg-amber-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Service Times
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto"></div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading service times...</p>
            </div>
          ) : services.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {services.map((service) => (
                  <div
                    key={service.id}
                    className="bg-white rounded-lg shadow-lg p-8 text-center"
                  >
                    <Clock className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {service.name}
                    </h3>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-red-600 mr-2" />
                        <span className="text-gray-600">{service.day}</span>
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-red-600">
                      {service.time}
                    </p>
                  </div>
                ))}
              </div>

              <div className="text-center mt-12">
                <Link
                  to="/services"
                  className="bg-red-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                >
                  View All Service Timings
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No Service Times Available
              </h3>
              <p className="text-gray-500 mb-6">
                Service schedules will appear here once they are added by the
                administrator.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section id="upcoming-events" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Upcoming Events
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto"></div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading events...</p>
            </div>
          ) : events.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {events.map((event) => (
                <div
                  key={event._id}
                  className="bg-amber-50 rounded-lg shadow-lg overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <Calendar className="w-5 h-5 text-red-600 mr-2" />
                      <span className="text-sm text-gray-600">
                        {event.date
                          ? new Date(event.date).toISOString().split("T")[0]
                          : "--"}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {event.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {event.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No Upcoming Events
              </h3>
              <p className="text-gray-500 mb-6">
                Check back soon for upcoming church events and activities.
              </p>
            </div>
          )}
          {!loading && events.length > 0 && (
            <div className="text-center mt-12">
              <Link
                to="/eventsandnews#upcoming-events"
                className="bg-red-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
              >
                View all Events
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* News Bulletin Section */}
      <section id="news-bulletin" className="py-20 bg-amber-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              News Bulletin
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto"></div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading news...</p>
            </div>
          ) : news.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {news.map((article) => (
                <div
                  key={article._id}
                  className="bg-white rounded-lg shadow-lg overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <Calendar className="w-5 h-5 text-red-600 mr-2" />
                      <span className="text-sm text-gray-600">
                        {article.date
                          ? new Date(article.date).toISOString().split("T")[0]
                          : new Date(article.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {article.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No News Available
              </h3>
              <p className="text-gray-500 mb-6">
                Stay tuned for the latest church news and announcements.
              </p>
            </div>
          )}
          {!loading && news.length > 0 && (
            <div className="text-center mt-12">
              <Link
                to="/eventsandnews#news-bulletin"
                className="bg-red-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
              >
                View all News
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Custom Dialog Modal */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto">
            <div className="flex items-center mb-4">
              {dialogType === "success" ? (
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              ) : (
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              )}
              <h3 className="text-lg font-semibold text-gray-900">
                {dialogType === "success" ? "Success" : "Error"}
              </h3>
            </div>
            <p className="text-gray-700 mb-6">{dialogMessage}</p>
            <div className="flex justify-end">
              <button
                onClick={closeDialog}
                className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Home Page Ad Carousel */}
      {showAdPopup && homePageAds.length > 0 && (
        <HomePageAdCarousel ads={homePageAds} onClose={handleCloseAdPopup} />
      )}
    </div>
  );
};

export default Home;
